<template>
	<!-- HAP-4127 — "Homepage Glow-Up". An artistic, editorial showcase of everything
	     Church & Family Life does. Lives at /showcase as a preview alongside the
	     production homepage (pages/home.vue); nothing here replaces the live '/'. -->
	<div class="hs-root overflow-hidden">
		<!-- ───────────────────────── HERO ─────────────────────────
		     The featured rotators are the star of the page — they lead, front and
		     center, framed by the aurora. A slim brand statement sits above them. -->
		<section class="hs-hero relative w-full overflow-hidden px-4 pb-16 pt-14 text-center sm:px-8 lg:pb-24 lg:pt-20">
			<!-- layered ambient glows -->
			<div class="hs-aurora pointer-events-none absolute inset-0" aria-hidden="true">
				<span class="hs-glow hs-glow-1" />
				<span class="hs-glow hs-glow-2" />
				<span class="hs-glow hs-glow-3" />
			</div>
			<div class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,white_100%)] dark:bg-[radial-gradient(ellipse_at_center,transparent_40%,black_100%)]" aria-hidden="true" />

			<div class="relative z-10 mx-auto flex max-w-7xl flex-col items-center">
				<div
					v-motion
					:initial="{ opacity: 0, y: 24 }"
					:enter="{ opacity: 1, y: 0, transition: { duration: 700 } }"
					class="mb-5 flex items-center gap-3 text-2xs font-semibold uppercase tracking-[0.35em] text-ncfic-blue-700 dark:text-ncfic-blue-200 sm:text-xs"
				>
					<span class="h-px w-8 bg-pp-gold/60" />
					Church &amp; Family Life
					<span class="h-px w-8 bg-pp-gold/60" />
				</div>

				<h1
					v-motion
					:initial="{ opacity: 0, y: 40 }"
					:enter="{ opacity: 1, y: 0, transition: { duration: 900, delay: 120 } }"
					class="font-serif text-4xl font-black leading-[1.02] tracking-tight text-gray-900 sm:text-5xl xl:text-6xl dark:text-white"
				>
					The sufficiency of Scripture for<!--
					--> <span class="hs-nowrap"><em class="hs-shimmer not-italic">church &amp; family life</em>.</span>
				</h1>

				<!-- Rotators: front and center, the opening visual of the page -->
				<div
					v-if="rotators.data?.length"
					v-motion
					:initial="{ opacity: 0, y: 40 }"
					:enter="{ opacity: 1, y: 0, transition: { duration: 900, delay: 300 } }"
					class="mt-8 w-screen overflow-hidden sm:mt-10"
				>
					<Rotator :rotators="rotators.data" class="w-full" />
				</div>

				<div
					v-motion
					:initial="{ opacity: 0, y: 24 }"
					:enter="{ opacity: 1, y: 0, transition: { duration: 900, delay: 480 } }"
					class="mt-10 flex flex-col items-center gap-4 sm:flex-row"
				>
					<button
						@click="scrollTo('explore')"
						class="group inline-flex items-center gap-3 rounded-full bg-gray-900 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-white shadow-xl shadow-gray-900/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl dark:bg-white dark:text-gray-900"
					>
						Explore our work
						<i class="fal fa-arrow-down transition-transform duration-300 group-hover:translate-y-1" />
					</button>
					<router-link
						to="/donate"
						class="inline-flex items-center gap-3 rounded-full border border-gray-300 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-gray-700 transition-all duration-300 hover:border-ncfic-blue-500 hover:text-ncfic-blue-700 dark:border-gray-700 dark:text-gray-300 dark:hover:border-ncfic-blue-400 dark:hover:text-ncfic-blue-200"
					>
						Partner with us
						<i class="fal fa-heart" />
					</router-link>
				</div>
			</div>
		</section>

		<!-- ─────────────────────── PODCASTS ─────────────────────── -->
		<section id="explore" class="relative w-full bg-gray-50 px-4 py-20 sm:px-8 lg:py-28 dark:bg-white/[0.02]" v-if="podcasts.data?.length">
			<div class="mx-auto max-w-7xl">
				<div class="mb-10 flex flex-col items-start justify-between gap-4 px-2 sm:flex-row sm:items-end">
					<div>
						<div class="text-2xs font-semibold uppercase tracking-[0.3em] text-pp-gold-text">Listen</div>
						<h2 class="mt-2 font-serif text-3xl font-black tracking-tight text-gray-900 sm:text-5xl dark:text-white">Podcasts for the whole household</h2>
					</div>
					<router-link to="/podcasts" class="shrink-0 text-sm font-semibold text-ncfic-blue-700 hover:underline dark:text-ncfic-blue-200">Browse all shows <i class="fal fa-long-arrow-right ml-1" /></router-link>
				</div>

				<div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
					<router-link
						v-for="(podcast, i) in podcasts.data.slice(0, 4)"
						:key="podcast.id"
						:to="`/podcasts/${podcast.slug}`"
						v-reveal="i"
						@click="store.openType = 'slideLeft'"
						class="group relative aspect-square overflow-hidden rounded-2xl bg-cover bg-center shadow-lg ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl dark:ring-white/10"
						:style="`background-image:url(${podcast?.image?.url})`"
					>
						<div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
						<div class="absolute bottom-0 left-0 right-0 translate-y-2 p-4 text-sm font-semibold text-white opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">{{ podcast.title }}</div>
					</router-link>
				</div>

				<!-- Latest episodes across every show, not just the CFL podcast -->
				<div class="mt-10" v-if="latestEpisodes.data?.length">
					<div class="mb-4 px-2 text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Latest episodes</div>
					<div class="snap-x snap-mandatory flex w-full space-x-4 overflow-x-auto px-2 pb-4">
						<router-link
							v-for="episode in latestEpisodes.data.filter((e) => e.podcast?.id).slice(0, 8)"
							:key="episode.id"
							:to="`/podcasts/${episode.podcast.id}/${episode.id}`"
							@click="store.openType = 'slideLeft'"
							class="group w-64 shrink-0 snap-start"
						>
							<div class="aspect-video w-full overflow-hidden rounded-xl bg-cover bg-center shadow-md ring-1 ring-black/5 transition-transform duration-500 group-hover:scale-[1.02] dark:ring-white/10" :style="`background-image:url(${episode?.image?.url || episode?.video?.thumbnail})`" />
							<div class="mt-2 line-clamp-2 text-sm font-semibold text-gray-800 dark:text-gray-200">{{ episode.title }}</div>
						</router-link>
					</div>
				</div>
			</div>
		</section>

		<!-- ─────────────────────── EVENTS ─────────────────────── -->
		<section class="relative w-full px-4 py-20 sm:px-8 lg:py-28" v-if="events.data?.length">
			<div class="mx-auto max-w-7xl">
				<div class="mb-10 flex flex-col items-start justify-between gap-4 px-2 sm:flex-row sm:items-end">
					<div>
						<div class="text-2xs font-semibold uppercase tracking-[0.3em] text-pp-gold-text">Gather</div>
						<h2 class="mt-2 font-serif text-3xl font-black tracking-tight text-gray-900 sm:text-5xl dark:text-white">Conferences &amp; events</h2>
						<p class="mt-3 max-w-lg text-gray-500 dark:text-gray-400">Come and sit under faithful preaching with the wider family of God.</p>
					</div>
					<router-link to="/events" class="shrink-0 text-sm font-semibold text-ncfic-blue-700 hover:underline dark:text-ncfic-blue-200">See the full calendar <i class="fal fa-long-arrow-right ml-1" /></router-link>
				</div>

				<div class="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
					<div v-for="(event, i) in events.data.slice(0, 3)" :key="event.id" v-reveal="i" class="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl dark:ring-white/10">
						<Event :homepage="true" :event="event" class="!p-0" @click="store.openType = 'popIn'" />
					</div>
				</div>
			</div>
		</section>

		<!-- ─────────────────── CHURCH NETWORK (map centerpiece) ─────────────────── -->
		<section class="relative w-full overflow-hidden bg-gradient-to-b from-ncfic-blue-500/5 to-transparent px-4 py-20 sm:px-8 lg:py-28 dark:from-ncfic-blue-500/10">
			<div class="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
				<div>
					<div class="text-2xs font-semibold uppercase tracking-[0.3em] text-pp-gold-text">Belong</div>
					<h2 class="mt-3 font-serif text-4xl font-black leading-tight tracking-tight text-gray-900 sm:text-6xl dark:text-white">Find a faithful church near you</h2>
					<p class="mt-5 max-w-md text-lg text-gray-600 dark:text-gray-400">A growing, nationwide network of gospel-centered, family-integrated churches — mapped so you can find your people.</p>
					<div class="mt-8 flex flex-wrap gap-4">
						<router-link to="/network?startSearch=true" class="inline-flex items-center gap-2 rounded-full bg-ncfic-blue-700 px-7 py-3.5 text-sm font-semibold uppercase tracking-widest text-white shadow-lg shadow-ncfic-blue-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ncfic-blue-900">
							Start your search <i class="fal fa-search" aria-hidden="true" />
						</router-link>
						<router-link to="/network/signup" class="inline-flex items-center gap-2 rounded-full border border-ncfic-blue-300 px-7 py-3.5 text-sm font-semibold uppercase tracking-widest text-ncfic-blue-700 transition-all duration-300 hover:bg-ncfic-blue-500/10 dark:border-ncfic-blue-700 dark:text-ncfic-blue-200">
							Add your church
						</router-link>
					</div>
				</div>
				<Facilitators v-slot="{ stateInfo, facilitators }" class="w-full">
					<div
						v-if="facilitators?.length"
						class="w-full"
						v-motion
						:initial="{ opacity: 0, scale: 0.95 }"
						:visible-once="{ opacity: 1, scale: 1, transition: { duration: 1200, type: 'spring' } }"
					>
						<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" class="w-full opacity-70" viewBox="150 45 1066 655" xml:space="preserve">
							<g>
								<template v-for="state in stateInfo" :key="state">
									<pattern v-if="state.advocate" :id="`hs-${state.state}-pattern`" width="8" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45 50 50)">
										<rect x="0" y="0" width="100%" height="100%" :class="state.class" />
										<line class="stroke-black/20 dark:stroke-gray-500" stroke-width="7px" y2="10" style="margin-top: 5px" />
									</pattern>
									<path :class="state.class" class="!cursor-default transition-all duration-500" :style="state.advocate ? `fill: url(#hs-${state.state}-pattern)` : ''" :d="state.path" />
								</template>
							</g>
						</svg>
					</div>
				</Facilitators>
			</div>
		</section>

		<!-- ─────────────────────── BOOKS / STORE ─────────────────────── -->
		<section class="relative w-full bg-gray-50 px-4 py-20 sm:px-8 lg:py-28 dark:bg-white/[0.02]" v-if="products.data?.length">
			<div class="mx-auto max-w-7xl">
				<div class="mb-10 flex flex-col items-start justify-between gap-4 px-2 sm:flex-row sm:items-end">
					<div>
						<div class="text-2xs font-semibold uppercase tracking-[0.3em] text-pp-gold-text">Read</div>
						<h2 class="mt-2 font-serif text-3xl font-black tracking-tight text-gray-900 sm:text-5xl dark:text-white">Books to build your home</h2>
					</div>
					<router-link to="/store" class="shrink-0 text-sm font-semibold text-ncfic-blue-700 hover:underline dark:text-ncfic-blue-200">Visit the store <i class="fal fa-long-arrow-right ml-1" /></router-link>
				</div>

				<div class="snap-x snap-mandatory flex w-full space-x-5 overflow-x-auto px-2 pb-4">
					<router-link
						v-for="product in products.data.slice(0, 10)"
						:key="product.id"
						:to="`/products/${product.id}`"
						@click="store.openType = 'popIn'"
						class="group w-40 shrink-0 snap-start sm:w-52"
					>
						<div class="aspect-[3/5] w-full overflow-hidden rounded-xl bg-cover bg-center shadow-lg ring-1 ring-black/5 transition-all duration-500 group-hover:-translate-y-1.5 group-hover:shadow-2xl dark:ring-white/10" :style="`background-image:url(${product?.image?.url})`" />
						<div class="mt-3 truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{{ product.name }}</div>
						<div class="truncate text-xs text-gray-500">{{ product.authors?.map((a) => a.title).join(', ') }}</div>
					</router-link>
				</div>
			</div>
		</section>

		<!-- ─────────────────── RESOURCES BY TOPIC ─────────────────── -->
		<section class="relative w-full px-4 py-20 sm:px-8 lg:py-28">
			<div class="mx-auto max-w-7xl">
				<div class="mb-10 text-center">
					<div class="text-2xs font-semibold uppercase tracking-[0.3em] text-pp-gold-text">Grow</div>
					<h2 class="mx-auto mt-3 max-w-2xl font-serif text-4xl font-black leading-tight tracking-tight text-gray-900 sm:text-5xl dark:text-white">Discipleship for every season of the family</h2>
				</div>
				<div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3">
					<router-link
						v-for="(subject, i) in donorResources.subjects"
						:key="subject.id"
						:to="`/family/${subject.id}`"
						v-reveal="i"
						@click="store.openType = 'popIn'"
						:class="topicGradients[i % topicGradients.length]"
						class="group relative flex h-40 items-end overflow-hidden rounded-2xl bg-gradient-to-br p-5 ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl dark:ring-white/10"
					>
						<!-- Topic art layers over a gradient base; the donor-resources images are
						     fragile Unsplash download links, so the gradient keeps the tile intentional if one fails. -->
						<div class="absolute inset-0 bg-cover bg-center opacity-90 transition-transform duration-[1.2s] group-hover:scale-110" :style="subject?.image ? `background-image:url(${subject.image})` : ''" />
						<div class="absolute inset-0 bg-gradient-to-t from-ncfic-blue-900/85 via-ncfic-blue-900/30 to-transparent" />
						<div class="relative z-10 font-serif text-lg font-bold leading-snug text-white drop-shadow">{{ subject.title }}</div>
					</router-link>
				</div>
			</div>
		</section>

		<!-- ─────────────── CHURCH RESOURCES BY TOPIC (HAP-5327) ───────────────
		     Same tiles as the family topics above, for Wesley's 12 Welcome Pastors categories. -->
		<section class="relative w-full bg-gray-50 px-4 py-20 sm:px-8 lg:py-28 dark:bg-white/[0.02]">
			<div class="mx-auto max-w-7xl">
				<div class="mb-10 text-center">
					<div class="text-2xs font-semibold uppercase tracking-[0.3em] text-pp-gold-text">Shepherd</div>
					<h2 class="mx-auto mt-3 max-w-2xl font-serif text-4xl font-black leading-tight tracking-tight text-gray-900 sm:text-5xl dark:text-white">Resources for pastors and the local church</h2>
				</div>
				<div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
					<router-link
						v-for="(subject, i) in churchResources.subjects"
						:key="subject.id"
						:to="`/pastors-welcome/${subject.id}`"
						v-reveal="i"
						@click="store.openType = 'popIn'"
						:class="topicGradients[i % topicGradients.length]"
						class="group relative flex h-40 items-end overflow-hidden rounded-2xl bg-gradient-to-br p-5 ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl dark:ring-white/10"
					>
						<div class="absolute inset-0 bg-cover bg-center opacity-90 transition-transform duration-[1.2s] group-hover:scale-110" :style="subject?.image ? `background-image:url(${subject.image})` : ''" />
						<div class="absolute inset-0 bg-gradient-to-t from-ncfic-blue-900/85 via-ncfic-blue-900/30 to-transparent" />
						<div class="relative z-10 font-serif text-lg font-bold leading-snug text-white drop-shadow">{{ subject.title }}</div>
					</router-link>
				</div>
				<div class="mt-10 text-center">
					<router-link to="/pastors-welcome" @click="store.openType = 'popIn'" class="inline-flex items-center gap-2 rounded-full border border-ncfic-blue-300 px-7 py-3.5 text-sm font-semibold uppercase tracking-widest text-ncfic-blue-700 transition-all duration-300 hover:bg-ncfic-blue-500/10 dark:border-ncfic-blue-700 dark:text-ncfic-blue-200">
						Welcome, Pastors <i class="fal fa-long-arrow-right" aria-hidden="true" />
					</router-link>
				</div>
			</div>
		</section>

		<!-- ─────────────────────── MISSION ─────────────────────── -->
		<section class="relative w-full overflow-hidden px-6 py-24 lg:py-36">
			<div class="hs-aurora pointer-events-none absolute inset-0 opacity-60" aria-hidden="true">
				<span class="hs-glow hs-glow-2" />
			</div>
			<div class="relative z-10 mx-auto max-w-3xl text-center">
				<img src="/images/logo-black.svg" alt="" class="mx-auto mb-8 w-20 opacity-80 dark:hidden" aria-hidden="true" />
				<img src="/images/logo-white.svg" alt="" class="mx-auto mb-8 hidden w-20 opacity-70 dark:block" aria-hidden="true" />
				<h2 class="font-serif text-3xl font-black leading-tight tracking-tight text-gray-900 sm:text-4xl dark:text-white">What is <em>Church &amp; Family Life</em>?</h2>
				<p class="mt-8 text-lg leading-relaxed text-gray-600 dark:text-gray-400 sm:text-xl">There is only one way to fight for the prosperity of the church and family in this world — to reveal the truth of Jesus Christ by proclaiming the Word of God.</p>
				<p class="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-400 sm:text-xl">We have a simple agenda: to proclaim the sufficiency of Scripture for church and family life, so that the true worship of God is restored in our cities and the gospel spreads from one generation to the next.</p>
				<router-link to="/about" class="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-ncfic-blue-700 hover:underline dark:text-ncfic-blue-200">Learn more about us <i class="fal fa-long-arrow-right" /></router-link>
			</div>
		</section>

		<!-- ─────────────────────── PARTNERS ─────────────────────── -->
		<section class="relative w-full px-8 py-16">
			<div class="mx-auto max-w-5xl">
				<div class="mb-10 text-center text-2xs font-semibold uppercase tracking-[0.3em] text-gray-400">Ministry partners</div>
				<div class="flex flex-wrap items-center justify-center gap-x-10 gap-y-8 opacity-60 grayscale transition-all duration-500 hover:opacity-100 hover:grayscale-0 dark:opacity-40">
					<a v-for="partner in partners" :key="partner.name" :href="partner.link" target="_blank" rel="noopener">
						<img :src="partner.logo" :alt="partner.name" :class="partner.name.includes('Founders') ? 'w-32' : 'w-24'" class="dark:brightness-0 dark:invert" />
					</a>
				</div>
			</div>
		</section>

		<!-- ─────────────────────── CLOSING CTA ─────────────────────── -->
		<section class="relative w-full px-6 pb-28 pt-8">
			<div class="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-gray-900 px-8 py-20 text-center shadow-2xl dark:bg-white/5 dark:ring-1 dark:ring-white/10">
				<div class="hs-aurora pointer-events-none absolute inset-0 opacity-40" aria-hidden="true">
					<span class="hs-glow hs-glow-1" />
					<span class="hs-glow hs-glow-3" />
				</div>
				<div class="relative z-10">
					<h2 class="mx-auto max-w-2xl font-serif text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl">Help us build the church and the family, one generation to the next.</h2>
					<p class="mx-auto mt-5 max-w-lg text-gray-300">Your support helps us equip churches and families across the world. Thank you for standing with us!</p>
					<router-link to="/donate" class="mt-10 inline-flex items-center gap-3 rounded-full bg-white px-9 py-4 text-sm font-semibold uppercase tracking-widest text-gray-900 shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl">
						Give today <i class="fal fa-heart" />
					</router-link>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import Rotator from '../home/rotator.vue'
import Event from '../events/event.vue'
import Facilitators from '../network/facilitators.vue'
import Mango from '../../helpers/mango'
import donorResources from '../../helpers/donor-resources.json'
import churchResources from '../../helpers/church-resources.json'
import topicGradients from '../../helpers/topicGradients'

let now = new Date()

export default {
	components: { Rotator, Event, Facilitators },
	inject: ['breakpoints', 'mode', 'darkMode', 'store'],
	data() {
		return {
			now,
			donorResources,
			churchResources,
			topicGradients,
			rotators: Mango.rotators.init({ search: { compareStatus: { not: 'closed' }, compareStartDate: { lessThan: now }, compareEndDate: { greaterThan: now } }, sort: { order: -1, startDate: -1 }, fields: ['title', 'image', 'titleImage', 'target', 'subtitle', 'rotatorType'] }),
			podcasts: Mango.podcasts.init({ limit: 12, search: { compareStatus: { not: 'closed' } }, sort: { startDate: -1 }, fields: ['title', 'image', 'slug'] }),
			// Latest episodes across every show (was filtered to the CFL podcast id only — HAP-4166).
			latestEpisodes: Mango.podcastEpisodes.init({ limit: 12, search: { compareStatus: { not: 'closed' }, compareStartDate: { lessThan: now } }, sort: { startDate: -1 }, fields: ['title', 'startDate', 'video', 'image', 'podcast'] }),
			products: Mango.products.init({ limit: 12, fields: ['name', 'id', 'image', 'authors', 'trendingScore', 'stock', 'price', 'msrp'], search: { compareStatus: { not: 'closed' }, compareStock: { greaterThan: 0 }, compareProductCategory: { in: ['book', 'audio', 'video'] } }, sort: { trendingScore: -1 } }),
			events: Mango.events.init({ limit: 5, fields: ['title', 'shortName', 'bannerImage', 'backgroundPosition', 'titleImage', 'speakers', 'startDate', 'address', 'endDate', 'status'], search: { compareStatus: { notIn: ['closed', 'hidden'] }, endDate: { $gt: now } }, sort: { startDate: 1 } }),
			partners: [
				{ name: 'HeartCry Missionary Society', link: 'https://heartcrymissionary.com/', logo: 'https://heartcrymissionary.com/wp-content/uploads/2019/06/heartcry-logo.png' },
				{ name: 'Chapel Library', link: 'https://www.chapellibrary.org/', logo: 'https://cfl-mango.s3.amazonaws.com/uploads/about/partners/chapel-library.png' },
				{ name: 'Founders Ministries', link: 'https://founders.org/', logo: 'https://cfl-mango.s3.amazonaws.com/uploads/about/partners/founders.png' },
				{ name: 'Generations', link: 'https://www.generations.org/', logo: 'https://cfl-mango.s3.amazonaws.com/uploads/about/partners/generations.png' },
				{ name: 'Free Grace Press', link: 'https://freegracepress.com/', logo: 'https://cfl-mango.s3.amazonaws.com/uploads/about/partners/free-grace-press.png' },
			],
		}
	},
	created() {
		document.title = 'Church & Family Life'
		this.$emit('navHidden', false)
	},
	methods: {
		scrollTo(id) {
			document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
		},
	},
}
</script>

<style src="./showcase.css"></style>
